import React, { useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  Search, Clock, MessageSquare, FlaskConical, Calculator,
  BarChart3, Heart, Zap, Apple, Loader2,
} from "lucide-react";
import { format } from "date-fns";function AnalysesDetails({ analyses, isDark }) {
  const { ai_queries, lab_analyses, calculator_results, daily_tracking, health_conditions, dil_profile, meal_entries } = analyses;
  const cardCls = `p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`;
  const labelCls = `text-xs font-semibold mb-2 flex items-center gap-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`;

  // Мемоизираме масива от DIL profile показатели за да избегнем filter/map в JSX.
  const dilSummary = useMemo(() => {
    if (!dil_profile) return [];
    return [
      { label: "DIL бюджет", value: dil_profile.personalized_dil, color: "#6366F1" },
      { label: "Фенотип", value: dil_profile.phenotype?.code, color: dil_profile.phenotype?.color || "#10B981" },
      { label: "Калории", value: dil_profile.final_calories && Math.round(dil_profile.final_calories), color: "#0EA5E9" },
      { label: "Цикъл фаза", value: dil_profile.cycle_phase, color: "#EC4899" },
    ].filter(d => d.value != null);
  }, [dil_profile]);

  return (
    <div className="space-y-6" data-testid="analyses-details">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: "AI заявки", count: ai_queries?.length || 0, color: "#6366F1", icon: MessageSquare },
          { label: "Лаб. анализи", count: lab_analyses?.length || 0, color: "#EF4444", icon: FlaskConical },
          { label: "Калкулатори", count: calculator_results?.length || 0, color: "#0EA5E9", icon: Calculator },
          { label: "Дни проследяване", count: daily_tracking?.length || 0, color: "#10B981", icon: BarChart3 },
          { label: "Състояния", count: health_conditions?.length || 0, color: "#F59E0B", icon: Heart },
        ].map(s => (
          <div key={s.label} className={`${cardCls} text-center`}>
            <s.icon className="w-4 h-4 mx-auto mb-1" style={{ color: s.color }} />
            <p className="text-xl font-bold" style={{ color: s.color }}>{s.count}</p>
            <p className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{s.label}</p>
          </div>
        ))}
      </div>

      {ai_queries?.length > 0 && (
        <div>
          <h4 className={labelCls}><MessageSquare className="w-3.5 h-3.5 text-[#6366F1]" /> AI Queries ({ai_queries.length})</h4>
          <ScrollArea className="h-[200px]">
            <div className="space-y-2">
              {ai_queries.map((q, i) => (
                <div key={q.id || i} className={cardCls}>
                  <p className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{q.query}</p>
                  {q.result?.final_response?.summary && (
                    <p className={`text-xs mt-1 line-clamp-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {q.result.final_response.summary.slice(0, 150)}...
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-1.5 text-[10px] text-gray-500">
                    <Clock className="w-3 h-3" />
                    {q.created_at ? format(new Date(q.created_at), "MMM d, HH:mm") : ""}
                    {q.result?.credits_used && <Badge variant="outline" className="text-[10px] h-4">{q.result.credits_used} cr</Badge>}
                    {q.result?.agents_used && <Badge variant="outline" className="text-[10px] h-4">{q.result.agents_used.join(", ")}</Badge>}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}

      {lab_analyses?.length > 0 && (
        <div>
          <h4 className={labelCls}><FlaskConical className="w-3.5 h-3.5 text-[#EF4444]" /> Lab Analyses ({lab_analyses.length})</h4>
          <div className="space-y-2">
            {lab_analyses.map((l, i) => (
              <div key={l.created_at || `lab-${i}`} className={cardCls}>
                <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{l.type || "Lab Upload"}</p>
                {l.summary && <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{l.summary.slice(0, 200)}</p>}
                {l.created_at && <p className="text-[10px] text-gray-500 mt-1">{format(new Date(l.created_at), "MMM d, yyyy HH:mm")}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {calculator_results?.length > 0 && (
        <div>
          <h4 className={labelCls}><Calculator className="w-3.5 h-3.5 text-[#0EA5E9]" /> Calculator Results ({calculator_results.length})</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {calculator_results.map((c, i) => (
              <div key={c.created_at || `calc-${i}`} className={cardCls}>
                <div className="flex items-center justify-between">
                  <p className={`text-sm font-medium capitalize ${isDark ? 'text-white' : 'text-gray-900'}`}>{(c.calculator_type || c.type || "calc").replace(/-/g, " ")}</p>
                  {c.created_at && <span className="text-[10px] text-gray-500">{format(new Date(c.created_at), "MMM d")}</span>}
                </div>
                {c.result && (
                  <div className="mt-1 text-xs text-gray-500">
                    {typeof c.result === "object" ? (
                      Object.entries(c.result).slice(0, 3).map(([k, v]) => (
                        <span key={k} className="mr-2">{k}: <strong className={isDark ? 'text-gray-300' : 'text-gray-700'}>{typeof v === "number" ? v.toFixed(1) : String(v)}</strong></span>
                      ))
                    ) : <span>{String(c.result)}</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {health_conditions?.length > 0 && (
        <div>
          <h4 className={labelCls}><Heart className="w-3.5 h-3.5 text-[#F59E0B]" /> Health Conditions ({health_conditions.length})</h4>
          <div className="space-y-2">
            {health_conditions.map((h, i) => (
              <div key={h.condition || h.name || `hc-${i}`} className={cardCls}>
                <p className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{h.condition || h.name || JSON.stringify(h).slice(0, 100)}</p>
                {h.created_at && <p className="text-[10px] text-gray-500 mt-1">{format(new Date(h.created_at), "MMM d, yyyy")}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {daily_tracking?.length > 0 && (
        <div>
          <h4 className={labelCls}><BarChart3 className="w-3.5 h-3.5 text-[#10B981]" /> Daily Tracking (last {daily_tracking.length} days)</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {daily_tracking.slice(0, 9).map((d) => (
              <div key={d.date} className={cardCls}>
                <p className={`text-xs font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{d.date || "N/A"}</p>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[10px] text-gray-500">
                  {d.totals?.calories != null && <span>Cal: <strong>{Math.round(d.totals.calories)}</strong></span>}
                  {d.totals?.protein != null && <span>P: <strong>{Math.round(d.totals.protein)}g</strong></span>}
                  {d.totals?.dil != null && <span>DIL: <strong>{d.totals.dil.toFixed(1)}</strong></span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {dil_profile && (
        <div>
          <h4 className={labelCls}><Zap className="w-3.5 h-3.5 text-[#6366F1]" /> DIL профил</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {dilSummary.map((d) => (
              <div key={d.label} className={`${cardCls} text-center`}>
                <p className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{d.label}</p>
                <p className="text-lg font-bold" style={{ color: d.color }}>{d.value}</p>
              </div>
            ))}
          </div>
          {dil_profile.phenotype?.label_bg && (
            <p className={`text-xs mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {dil_profile.phenotype.label_bg} — {dil_profile.phenotype.focus_bg}
            </p>
          )}
        </div>
      )}

      {meal_entries?.length > 0 && (
        <div>
          <h4 className={labelCls}><Apple className="w-3.5 h-3.5 text-[#10B981]" /> Хранене (последни {meal_entries.length} записа)</h4>
          <ScrollArea className="h-[250px]">
            <div className="space-y-2">
              {meal_entries.map((m, i) => (
                <div key={`${m.date}-${m.meal_type}-${i}`} className={cardCls}>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {m.date} — {m.meal_type || "Хранене"}
                    </span>
                    {m.totals?.dil != null && (
                      <Badge variant="outline" className="text-[10px] h-4 text-[#6366F1] border-[#6366F1]">
                        DIL: {typeof m.totals.dil === 'number' ? m.totals.dil.toFixed(1) : m.totals.dil}
                      </Badge>
                    )}
                  </div>
                  {m.foods?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {m.foods.slice(0, 6).map((f, fi) => (
                        <span key={f.food || f.name || `food-${fi}`} className={`text-[10px] px-1.5 py-0.5 rounded ${isDark ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                          {f.food || f.name || f} {f.portion_g ? `(${f.portion_g}g)` : ""}
                        </span>
                      ))}
                      {m.foods.length > 6 && <span className="text-[10px] text-gray-500">+{m.foods.length - 6}</span>}
                    </div>
                  )}
                  {m.totals && (
                    <div className="flex gap-3 mt-1 text-[10px] text-gray-500">
                      {m.totals.calories != null && <span>Кал: {Math.round(m.totals.calories)}</span>}
                      {m.totals.protein != null && <span>П: {Math.round(m.totals.protein)}г</span>}
                      {m.totals.carbs != null && <span>В: {Math.round(m.totals.carbs)}г</span>}
                      {m.totals.fat != null && <span>М: {Math.round(m.totals.fat)}г</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}

      {(!ai_queries?.length && !lab_analyses?.length && !calculator_results?.length && !daily_tracking?.length && !health_conditions?.length) && (
        <div className="text-center py-12 text-gray-400">
          <FlaskConical className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No analyses data found for this user</p>
        </div>
      )}
    </div>
  );
}

export default function AdminAnalysesPanel({ isDark, users, searchTerm, setSearchTerm, API }) {
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [analyses, setAnalyses] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchAnalyses = async (userId) => {
    setSelectedUserId(userId);
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/users/${userId}/analyses`);
      setAnalyses(data);
    } catch (e) {
      toast.error("Failed to fetch analyses");
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => users.filter(u =>
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  ), [users, searchTerm]);

  const selectedUser = users.find(u => u.id === selectedUserId);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="analyses-panel">
      <Card className={`lg:col-span-1 ${isDark ? 'bg-gray-800 border-gray-700' : 'card-elevated border-0'}`}>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <FlaskConical className="w-5 h-5 text-[#6366F1]" />
            User Analyses
          </CardTitle>
          <div className="relative mt-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Search users..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`pl-10 ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-[#F8FAFC]'}`}
              data-testid="analyses-search" />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="h-[500px]">
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {filtered.map((u) => (
                <button key={u.id} onClick={() => fetchAnalyses(u.id)}
                  className={`w-full text-left px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${selectedUserId === u.id ? 'bg-[#6366F1]/5 dark:bg-[#6366F1]/10' : ''}`}
                  data-testid={`analyses-user-${u.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#6366F1] to-[#0EA5E9] flex items-center justify-center">
                      <span className="text-white text-xs font-medium">{u.name?.charAt(0)?.toUpperCase() || "?"}</span>
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{u.name}</p>
                      <p className="text-xs text-gray-500">{u.email}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      <Card className={`lg:col-span-2 ${isDark ? 'bg-gray-800 border-gray-700' : 'card-elevated border-0'}`}>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#0EA5E9]" />
            {selectedUser ? `${selectedUser.name} — Analyses` : "Select a user"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-[#6366F1]" />
            </div>
          ) : analyses ? (
            <AnalysesDetails analyses={analyses} isDark={isDark} />
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <FlaskConical className="w-16 h-16 mb-4 opacity-30" />
              <p>Select a user to view their analyses</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
